import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

/**
 * Coming-Soon gate shown at "/" while the domain goes live.
 *
 * Insider bypass:
 *   • Visit "/?insider=1" to unlock and remember (localStorage) — see the
 *     real Landing page on subsequent visits from this device.
 *   • Visit "/?insider=0" to re-lock.
 */
export default function ComingSoon() {
  const [form, setForm] = useState({ name: "", society_name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/public/enquiry", { ...form, message: "Coming Soon — early-access interest" });
      setDone(true);
      setForm({ name: "", society_name: "", phone: "", email: "" });
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-sand-50 text-ink font-sans flex flex-col">
      {/* subtle background grain */}
      <div className="pointer-events-none fixed inset-0 grain opacity-40" />

      <header className="relative z-10 max-w-6xl mx-auto w-full px-6 lg:px-10 py-8" data-testid="cs-header">
        <BrandMark variant="landing" size={48} />
      </header>

      <main className="relative z-10 flex-1 flex items-center">
        <div className="max-w-6xl mx-auto w-full px-6 lg:px-10 grid lg:grid-cols-12 gap-12 py-10 lg:py-16 items-start">
          {/* Copy */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs uppercase tracking-widest text-stone-600 mb-8">
              <Sparkles size={12} className="text-terracotta" />
              Coming Soon
            </div>
            <h1 className="editorial text-5xl sm:text-6xl lg:text-[76px] leading-[1.02] font-medium tracking-tight text-ink">
              A new way for<br/>housing and commercial<br/>societies to <span className="italic text-terracotta">run themselves.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-stone-600 leading-relaxed">
              The Society OS is launching soon — a modern platform to change how residential and
              commercial societies manage invoicing, complaints, notices, applications, and
              committee governance, all in one place.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-stone-500">
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-terracotta"/> Bank-grade encryption</span>
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-terracotta"/> India-only data residency</span>
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-terracotta"/> Made in India</span>
            </div>
          </div>

          {/* Early-access capture */}
          <div className="lg:col-span-5">
            <div className="bg-white border border-stone-200 rounded-3xl p-8 lg:p-10 shadow-[0_8px_30px_rgb(30,63,99,0.06)]" data-testid="cs-form-card">
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-3">Get early access</div>
              <h2 className="editorial text-2xl leading-snug mb-2">Be first to know when we go live.</h2>
              <p className="text-stone-600 text-sm leading-relaxed mb-6">
                Leave your details — we'll reach out with a private demo before the public launch.
              </p>

              {done ? (
                <div className="rounded-xl bg-terracotta/10 border border-terracotta/20 text-ink p-5" data-testid="cs-done">
                  <div className="editorial text-lg mb-1">Thank you.</div>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    You're on the early-access list. We'll be in touch shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="cs-form">
                  <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="cs-name"/>
                  <Field label="Society name" value={form.society_name} onChange={(v) => setForm({ ...form, society_name: v })} testid="cs-society"/>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="cs-phone"/>
                    <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="cs-email"/>
                  </div>
                  <button type="submit" disabled={busy} data-testid="cs-submit"
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-6 py-3.5 text-base font-medium transition-colors">
                    {busy ? "Sending…" : "Notify me"} <ArrowRight size={18}/>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 max-w-6xl mx-auto w-full px-6 lg:px-10 py-10 border-t border-stone-200/70 mt-6" data-testid="cs-footer">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-stone-500">
          <div>A product of <span className="font-semibold text-ink">Reliserve Platforms Private Limited</span></div>
          <div>© {new Date().getFullYear()} · Made in India</div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">{label}{required && " *"}</label>
      <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-[15px] focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none"/>
    </div>
  );
}
